/**
 * Company Logo Utilities
 * 
 * Centralized logic for handling company logos across the application.
 * Must match the logic in scripts/data_processing/download-logos.ts
 */

import { BASE_URL } from '../../utils/constants/url-constants';
import { isTyomarkkinatoriCompanyId } from './is-tyomarkkinatori-company-id';

/**
 * Formats company name to kebab-case for logo URLs
 * This MUST match the logic in scripts/data_processing/download-logos.ts
 */
export function formatCompanyNameToKebabCase(companyName: string): string {
  if (!companyName || typeof companyName !== 'string') {
    return 'company';
  }
  return companyName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Gets the company logo URL (constructed path; logos are served as WebP from getCompanyLogoDefaultSrc).
 *
 * @param companyId - The company name/ID
 * @returns URL path to the 128px WebP logo, matching the sizes the logo pipeline actually generates (64/128/200)
 */
export function getCompanyLogoUrl(companyId: string): string {
  if (!companyId || typeof companyId !== 'string') {
    return '/company-logo-default.svg';
  }
  const kebab = formatCompanyNameToKebabCase(companyId);
  return `/logos/${kebab}-128.webp`;
}

/**
 * Gets the srcset string for responsive company logos (WebP format, multiple sizes).
 * 
 * Logos are stored in S3 at: {BASE_URL}/logos/{companyId}-{size}.webp where size is 64, 128, or 200.
 * Uses absolute URLs so logos work in both development and production.
 * 
 * Special case: Työmarkkinatori uses their official SVG logo (no srcset needed)
 * 
 * @param companyId - The company name/ID (will be kebab-cased)
 * @param isTyomarkkinatori - Optional flag indicating if this is a tyomarkkinatori job
 * @returns srcset string for the logo, or empty string if companyId is invalid or tyomarkkinatori
 * 
 * @example
 * getCompanyLogoSrcset('Company Name') 
 * // Returns: 'https://www.jobcrawls.com/logos/company-name-64.webp 64w, https://www.jobcrawls.com/logos/company-name-128.webp 128w, https://www.jobcrawls.com/logos/company-name-200.webp 200w'
 */
export function getCompanyLogoSrcset(
  companyId: string,
  isTyomarkkinatori?: boolean | 'true' | 'false',
  /** Appends `?v=` to each URL — for the moment right after an employer's own logo upload, so
   *  their browser doesn't keep serving the previous image from its own HTTP cache while
   *  waiting on the CloudFront invalidation to propagate. Omit for every other caller. */
  cacheBustVersion?: string,
): string {
  if (!companyId || typeof companyId !== 'string') {
    return '';
  }

  // Special case: Työmarkkinatori uses SVG (no srcset needed)
  if (isTyomarkkinatori === true || isTyomarkkinatori === 'true') {
    return ''; // SVG doesn't need srcset
  }

  if (isTyomarkkinatoriCompanyId(companyId)) {
    return ''; // SVG doesn't need srcset
  }

  const kebab = formatCompanyNameToKebabCase(companyId);
  const suffix = cacheBustVersion ? `?v=${encodeURIComponent(cacheBustVersion)}` : '';
  return `${BASE_URL}/logos/${kebab}-64.webp${suffix} 64w, ${BASE_URL}/logos/${kebab}-128.webp${suffix} 128w, ${BASE_URL}/logos/${kebab}-200.webp${suffix} 200w`;
}

/**
 * Gets the default src (fallback) for a company logo.
 * Uses the 128px size as the default since it's a good middle ground.
 * Uses absolute URLs so logos work in both development and production.
 * 
 * Special case: Työmarkkinatori uses their official SVG logo
 * 
 * @param companyId - The company name/ID (will be kebab-cased)
 * @param isTyomarkkinatori - Optional flag indicating if this is a tyomarkkinatori job
 * @returns URL to the 128px WebP logo, or default logo if companyId is invalid
 */
export function getCompanyLogoDefaultSrc(
  companyId: string,
  isTyomarkkinatori?: boolean | 'true' | 'false',
  /** See getCompanyLogoSrcset's cacheBustVersion — same purpose, same caveat. */
  cacheBustVersion?: string,
): string {
  if (!companyId || typeof companyId !== 'string') {
    return '/company-logo-default.svg';
  }

  // Special case: Työmarkkinatori uses their official SVG logo (relative path so it works in dev and prod)
  if (isTyomarkkinatori === true || isTyomarkkinatori === 'true') {
    return '/tyomarkkinatori.svg';
  }

  if (isTyomarkkinatoriCompanyId(companyId)) {
    return '/tyomarkkinatori.svg';
  }

  const kebab = formatCompanyNameToKebabCase(companyId);
  const suffix = cacheBustVersion ? `?v=${encodeURIComponent(cacheBustVersion)}` : '';
  return `${BASE_URL}/logos/${kebab}-128.webp${suffix}`;
}

/**
 * Builds an ordered list of logo sources to try (all generated from company name).
 *
 * Order: constructed URL, then default logo.
 *
 * @deprecated Use getCompanyLogoSrcset and getCompanyLogoDefaultSrc instead for WebP logos
 *
 * @param companyName - The company name/ID
 * @returns Array of URLs to try in order
 */
export function getCompanyLogoSources(companyName: string): string[] {
  if (!companyName || typeof companyName !== 'string') {
    return ['/company-logo-default.svg'];
  }
  const constructedUrl = getCompanyLogoUrl(companyName);
  return [constructedUrl, '/company-logo-default.svg'];
}
